import { zonoApi } from '../api/zonoApi';
import {
  AudienceRequest,
  AudienceResponse,
  CfaRequest,
  CfaResponse,
  CreateCustomerRequest,
  CreateCustomerResponse,
  CustomerDetailRequest,
  CustomerDetailResponse,
  CustomerFiltersResponse,
  CustomerListRequest,
  CustomerListResponse,
  CustomerSearchListRequest,
  CustomersReportRequest,
  CustomersReportResponse,
  DivisionRequest,
  DivisionResponse,
  DocumentRequestHub,
  ExludeActiveCustomerRequest,
  GetActiveCustomersRequest,
  GetActiveCustomersResponse,
  ParseCustomerResponse,
  State,
  TeamSearchRequest,
  TeamSearchResponse,
  UpdateCustomerActiveStatusRequest,
} from './customer-model';
import _pickBy from 'lodash/pickBy';
import _identity from 'lodash/identity';
import { getQueryParams } from '@zonofi/common/functions/get-query-param';
import {
  FilterRoute,
  FilterValue,
  getTreeMappingFilters,
  ListFilterKey,
  ListFiltersMap,
  updateFiltersByRoute,
} from '../filter';

export const customerApi = zonoApi.injectEndpoints({
  endpoints: (builder) => ({
    getActiveCustomers: builder.query<
      GetActiveCustomersResponse,
      GetActiveCustomersRequest
    >({
      query: (payload) => {
        return {
          url: `hub/communication-v2/api/active-users`,
          method: 'GET',
          params: {
            ...payload,
            workspaceId: payload.sellerWorkspaceId,
          },
        };
      },
    }),
    getCustomerAudience: builder.query<AudienceResponse, AudienceRequest>({
      query: (args) => ({
        url: `hub/orgs/api/agent/audience/${args.sellerWorkspaceId}?${getQueryParams(args)}`,
        method: 'GET',
      }),
    }),
    exludeActiveCustomer: builder.mutation<
      { status: string; statusCode: number },
      ExludeActiveCustomerRequest
    >({
      query: ({ inviteIds, ...params }) => ({
        url: `hub/orgs/api/agent/audience/update`,
        method: 'POST',
        params,
        body: { inviteIds },
      }),
    }),
    getCustomerDetails: builder.query<
      CustomerDetailResponse,
      CustomerDetailRequest
    >({
      query: (payload) => {
        return {
          url: `/hub/orgs/api/customer/details`,
          method: 'GET',
          params: payload,
        };
      },
    }),
    uploadWorkspaceDocuments: builder.mutation<
      { presignedUrl: string },
      DocumentRequestHub
    >({
      query(arg) {
        const { workspaceId, ...body } = arg;
        return {
          url: `workspaces/documents/${workspaceId}`,
          method: 'POST',
          body: body,
        };
      },
    }),
    getCustomerList: builder.query<CustomerListResponse, CustomerListRequest>({
      query(params: CustomerListRequest) {
        const { workspaceId, ...paramObject } = params;
        const param: any = {
          ...paramObject,
          sellerWorkspaceId: workspaceId,
        };
        const queryParams = getQueryParams(param);
        return {
          url: `hub/orgs/api/customers/${workspaceId}?${queryParams}`,
          method: 'GET',
        };
      },
    }),
    updateCustomerActiveStatus: builder.mutation<
      { status?: string; statusCode?: number },
      UpdateCustomerActiveStatusRequest
    >({
      query: ({ customerId, isActive }) => ({
        url: `/customer/${customerId}`,
        method: 'PATCH',
        params: { isActive },
      }),
    }),
    getCustomerListSearch: builder.query<
      CustomerListResponse,
      CustomerSearchListRequest
    >({
      query(params: CustomerSearchListRequest) {
        const { workspaceId, ...paramObject } = params;
        const param: any = _pickBy(
          { ...paramObject, sellerWorkspaceId: workspaceId },
          _identity
        );
        const queryParams = Object.keys(param)
          .map((key) => {
            const qString: string[] = [];
            if (Array.isArray(param[key])) {
              //@ts-ignore
              for (let index = 0; index < params[key].length; index++) {
                const value = param[key][index];
                qString.push(
                  `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
                );
              }
              return qString.join('&');
            }
            return `${encodeURIComponent(key)}=${encodeURIComponent(param[key])}`;
          })
          .join('&');
        return {
          url: `hub/orgs/api/customers/${workspaceId}?${queryParams}`,
          method: 'GET',
          cache: 'no-cache',
        };
      },
    }),
    getTeamSearch: builder.query<TeamSearchResponse, TeamSearchRequest>({
      query: (args) => ({
        url: `hub/orgs/api/team/list?${getQueryParams({
          sellerWorkspaceId: args.workspaceId,
          ...args,
        })}`,
        method: 'GET',
      }),
    }),
    getCfaList: builder.query<CfaResponse, CfaRequest>({
      query: (args) => {
        const { workspaceId, ...params } = args;
        return {
          url: `hub/orgs/api/cfa`,
          method: 'GET',
          params: { ...params, sellerWorkspaceId: workspaceId },
        };
      },
    }),
    getAllStates: builder.query<State[], void>({
      query() {
        return { url: 'states', method: 'GET' };
      },
    }),
    createNewCustomer: builder.mutation<
      CreateCustomerResponse,
      CreateCustomerRequest
    >({
      query: (body) => ({
        url: `hub/orgs/api/customers/create?sellerWorkspaceId=${body.senderWorkspaceId}`,
        method: 'POST',
        body,
      }),
    }),
    getDivisions: builder.query<DivisionResponse, DivisionRequest>({
      query(arg) {
        const { workspaceId, ...params } = arg;
        const queryParams = getQueryParams({
          ...params,
          sellerWorkspaceId: workspaceId,
          includeHeadDivision: true,
        });
        return {
          url: `hub/orgs/api/division?workspaceId=${workspaceId}&${queryParams}`,
          method: 'GET',
        };
      },
    }),
    getCustomerFilter: builder.query<
      CustomerFiltersResponse,
      { sellerWorkspaceId: string }
    >({
      query({ sellerWorkspaceId }) {
        return {
          url: `/hub/orgs/api/customers/filters`,
          method: 'GET',
          params: {
            sellerWorkspaceId,
          },
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then((response) => {
            const { data } = response;
            const { divisions = [], cfa = [] } = data || {};

            const cfaValues: FilterValue[] = cfa?.map((item) => ({
              id: item?.id,
              label: item?.supplierName,
              selected: false,
            }));
            const divisionValues: FilterValue[] = divisions?.map((item) => ({
              id: item?.id,
              label: item?.name,
              selected: false,
            }));

            const listFilter: Partial<ListFiltersMap> = {};
            if (cfaValues.length > 0) {
              listFilter[ListFilterKey.Cfa] = {
                multiSelect: true,
                values: cfaValues,
              };
            }
            if (divisionValues.length > 0) {
              listFilter[ListFilterKey.Divisions] = {
                multiSelect: true,
                values: divisionValues,
              };
            }

            const filters = getTreeMappingFilters(data);

            dispatch(
              updateFiltersByRoute({
                route: FilterRoute.Customer,
                filter: {
                  listFilter,
                  treeFilter: filters?.treeFilter,
                },
              })
            );
          })
          .catch((error) => console.log('customer filter error', error));
      },
    }),
    getCustomerReport: builder.mutation<
      CustomersReportResponse[],
      CustomersReportRequest
    >({
      query: (args) => {
        return {
          url: `/hub/reports/api/v1/customer-metrics-report`,
          method: 'POST',
          params: args.params,
          body: args.body,
        };
      },
      transformResponse: (response: { data: CustomersReportResponse[] }) =>
        response.data,
    }),
    parseCustomer: builder.mutation<
      ParseCustomerResponse,
      {
        formData: FormData;
        sellerWorkspaceId: string;
      }
    >({
      query: (args) => ({
        url: `/hub/bot/api/v1/parse/customer?sellerWorkspaceId=${args.sellerWorkspaceId}`,
        method: 'POST',
        body: args.formData,
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  useLazyGetActiveCustomersQuery,
  useExludeActiveCustomerMutation,
  useLazyGetCustomerDetailsQuery,
  useUploadWorkspaceDocumentsMutation,
  useLazyGetCustomerListQuery,
  useUpdateCustomerActiveStatusMutation,
  useLazyGetCustomerListSearchQuery,
  useLazyGetTeamSearchQuery,
  useLazyGetCfaListQuery,
  useLazyGetAllStatesQuery,
  useCreateNewCustomerMutation,
  useLazyGetDivisionsQuery,
  useLazyGetCustomerFilterQuery,
  useLazyGetCustomerAudienceQuery,
  useGetCustomerReportMutation,
  useParseCustomerMutation,
} = customerApi;
